import Colors from '../../utils/Colors.js'

// The server sends the hash of each frontend bundle whenever a socket connects. A deploy restarts the server, so the
// socket reconnects: when the hash then differs from the one this page started with, a new version is live.
// The first hash seen, on the connection made while the page loads, is the version this page runs on.
// Kept per bundle: the messenger bundle also contains the planner's socket module, so both checks run on its pages.
const loadedBuilds = {}
let bannerShown = false

/**
 * @param {'main' | 'messenger'} name the bundle to check, skipped when this page does not run it
 * @param {Record<string, string | undefined>} builds hash of each bundle as reported by the server
 * @param {{canReload?: () => boolean}} [options] canReload returns false while reloading would lose the user's work
 */
export function checkBuild(name, builds, {canReload} = {}) {
    const build = builds?.[name]

    if (!build || !document.querySelector(`script[src="/build/${name}.js"]`)) return

    if (!loadedBuilds[name]) {
        loadedBuilds[name] = build
        return
    }

    if (build === loadedBuilds[name]) return

    // During a rolling restart one server can still report the old hash, so reload only once per new version
    try {
        if (sessionStorage.reloadedForBuild === build) return
    } catch {
        // Storage blocked, reload anyway
    }

    if (canReload?.()) {
        reload(build)
        return
    }

    // Already waiting for the user, a later connection must not start a second timer or banner
    if (bannerShown) return

    if (canReload) {
        // Reload as soon as the user has finished, the banner covers the case they never do
        const interval = setInterval(() => {
            if (canReload()) {
                clearInterval(interval)
                reload(build)
            }
        }, 2000)
    }

    showBanner(build)
}

function reload(build) {
    try {
        sessionStorage.reloadedForBuild = build
    } catch {
        // Storage blocked, nothing to remember
    }

    window.location.reload()
}

function showBanner(build) {
    bannerShown = true

    // Floating card at the bottom so it does not cover the page header, the text wraps on narrow messenger screens
    const banner = document.createElement('div')
    banner.style.cssText = `position: fixed; bottom: 32px; left: 50%; transform: translateX(-50%); z-index: 100000; display: flex; align-items: center; gap: 20px; padding: 12px 12px 12px 24px; background: ${Colors.white}; color: ${Colors.textDark}; border: 1px solid ${Colors.grey30}; width: max-content; max-width: calc(100vw - 32px); box-sizing: border-box; border-radius: 28px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16); font-family: Roboto, sans-serif; font-size: 16px; font-weight: 500; letter-spacing: 0.1px;`

    const text = document.createElement('span')
    text.textContent = 'Er is een nieuwe versie beschikbaar'

    const button = document.createElement('button')
    button.textContent = 'Herladen'
    button.style.cssText = `border: none; border-radius: 999px; background: ${Colors.successDark}; color: ${Colors.white}; padding: 8px 20px; font: inherit; white-space: nowrap; flex-shrink: 0; cursor: pointer;`
    button.addEventListener('click', () => reload(build))

    banner.append(text, button)
    document.body.append(banner)

    banner.animate([
        {opacity: 0, transform: 'translate(-50%, 16px)'},
        {opacity: 1, transform: 'translate(-50%, 0)'}
    ], {duration: 250, easing: 'ease-out'})
}
